import type { NoteName, Scale, ScaleType } from '@tunasong/schemas'
import { getNoteChromaticIndex } from '../note/note-lib.js'

/**
 * Key spelling for the key selector (GH #2764; spec docs/ux/2026-10-24-key-selector.md §3, Bowie's verdict §4).
 *
 * A key is spelled degree by degree on successive letters, so its accidental count and any double sharp or flat
 * follow from the scale itself. The root grid's labels are derived from Bowie's rule: per pitch, the spelling with
 * the fewest accidentals and no double sharp or flat; a tie takes the Major row's spelling, and in Major itself
 * F♯ beats G♭ (Bowie: the usual chart spelling). Nothing here is a hardcoded table.
 */

const letters = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const
const naturalPitch: Record<(typeof letters)[number], number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }

type Heptatonic =
  'major' | 'minor' | 'harmonicminor' | 'melodicminor' | 'dorian' | 'phrygian' | 'lydian' | 'mixolydian' | 'locrian'

/** Semitones above the root for each degree */
const patterns: Record<Heptatonic, number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  harmonicminor: [0, 2, 3, 5, 7, 8, 11],
  melodicminor: [0, 2, 3, 5, 7, 9, 11],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  locrian: [0, 1, 3, 5, 6, 8, 10],
}

/** Pentatonics are spelled from their parent scale (Bowie), and the pentatonic picks its degrees from it */
const pentatonicDegrees = { majorpentatonic: [0, 1, 2, 4, 5], minorpentatonic: [0, 2, 3, 4, 6] } as const

/** The heptatonic scale a type is spelled from; ionian and aeolian are other names for major and minor */
const parentOf = (type: ScaleType | undefined): Heptatonic => {
  switch (type) {
    case undefined:
    case 'major':
    case 'ionian':
    case 'majorpentatonic':
      return 'major'
    case 'minor':
    case 'aeolian':
    case 'minorpentatonic':
      return 'minor'
    default:
      return type
  }
}

/**
 * The scale whose spelling decides the root label and its validity. Harmonic and melodic minor follow minor
 * (Bowie, spec §4.1), so G♯ harmonic minor keeps G♯ although its raised seventh is F𝄪; the modes are spelled as
 * themselves.
 */
const spellingFamily = (type: ScaleType | undefined): Heptatonic => {
  const parent = parentOf(type)
  return parent === 'harmonicminor' || parent === 'melodicminor' ? 'minor' : parent
}

const accidentalText = (offset: number) => (offset > 0 ? '#'.repeat(offset) : 'b'.repeat(-offset))

const parse = (root: string) => {
  const letter = root[0] as (typeof letters)[number]
  const accidentals = root.slice(1)
  const offset = [...accidentals].reduce((sum, char) => sum + (char === '#' ? 1 : char === 'b' ? -1 : 0), 0)
  return { letter, offset }
}

/** Signed offset from a natural, in -6..5 */
const wrap = (semitones: number) => ((((semitones + 6) % 12) + 12) % 12) - 6

export interface SpelledKey {
  notes: string[]
  /** The number of sharps or flats across the scale's notes (a double counts twice) */
  accidentals: number
  /** Whether any note needs a double sharp or flat */
  double: boolean
}

export const spellKey = (root: NoteName | string, type?: ScaleType): SpelledKey => {
  const { letter, offset } = parse(root)
  const rootPitch = naturalPitch[letter] + offset
  const start = letters.indexOf(letter)
  const parent = parentOf(type)
  const offsets = patterns[parent].map((interval, degree) => {
    const degreeLetter = letters[(start + degree) % 7] ?? 'C'
    return { degreeLetter, offset: wrap(rootPitch + interval - naturalPitch[degreeLetter]) }
  })
  const picked =
    type === 'majorpentatonic' || type === 'minorpentatonic'
      ? pentatonicDegrees[type].map(degree => offsets[degree]).filter(x => x !== undefined)
      : offsets
  return {
    notes: picked.map(({ degreeLetter, offset: o }) => `${degreeLetter}${accidentalText(o)}`),
    accidentals: offsets.reduce((sum, { offset: o }) => sum + Math.abs(o), 0),
    double: offsets.some(({ offset: o }) => Math.abs(o) >= 2),
  }
}

export const isValidSpelling = (root: NoteName | string, type?: ScaleType) =>
  !spellKey(root, spellingFamily(type)).double

/** Every single-accidental spelling of a pitch: the natural (if any), the sharp and the flat, E♯ and C♭ included */
const candidates = (pitch: number) =>
  letters.flatMap(letter => {
    const offset = wrap(pitch - naturalPitch[letter])
    return Math.abs(offset) <= 1 ? [`${letter}${accidentalText(offset)}`] : []
  })

const majorTieBreak = (spellings: string[]) =>
  /** Bowie: F♯ over G♭ (both six); any other tie in Major is resolved by accidental count alone */
  spellings.find(spelling => spelling.includes('#')) ?? spellings[0]

const bestSpelling = (pitch: number, parent: Heptatonic): string => {
  const valid = candidates(pitch)
    .map(spelling => ({ spelling, key: spellKey(spelling, parent) }))
    .filter(({ key }) => !key.double)
  const fewest = Math.min(...valid.map(({ key }) => key.accidentals))
  const tied = valid.filter(({ key }) => key.accidentals === fewest).map(({ spelling }) => spelling)
  if (tied.length <= 1) {
    return tied[0] ?? candidates(pitch)[0] ?? 'C'
  }
  if (parent === 'major') {
    return majorTieBreak(tied)
  }
  const majorChoice = bestSpelling(pitch, 'major')
  return tied.includes(majorChoice) ? majorChoice : (tied[0] ?? majorChoice)
}

/** The 12 root labels, chromatic from C, spelled for a scale type (spec §3.1) */
export const rootSpellingsFor = (type?: ScaleType): NoteName[] =>
  Array.from({ length: 12 }, (_, pitch) => bestSpelling(pitch, spellingFamily(type)) as NoteName)

/**
 * Never offered as a root (Bowie): E♯, B♯ and F♭ are never the tonic of a key signature, only degrees inside one.
 * C♭ is (C♭ major is a standard signature). A legacy stored one still reads back as stored if it's valid.
 */
const neverOffered = new Set(['E#', 'B#', 'Fb'])

/** The other valid spelling of this key's root, for "Spell as …" (spec §3.3); null when there is none */
export const alternateSpelling = (root: NoteName, type?: ScaleType): NoteName | null => {
  const pitch = getNoteChromaticIndex(root)
  const other = candidates(pitch).find(
    spelling => spelling !== root && !neverOffered.has(spelling) && isValidSpelling(spelling, type)
  )
  return (other as NoteName | undefined) ?? null
}

/** Keep the root's spelling for a new type unless it would need a double accidental (spec §3.4) */
export const respellForType = (root: NoteName, type?: ScaleType): NoteName =>
  isValidSpelling(root, type) ? root : (rootSpellingsFor(type)[getNoteChromaticIndex(root)] ?? root)

/** The root as the read-back shows it: as stored when valid for its type, else the grid's spelling (§3.2) */
export const displayRoot = (scale: Pick<Scale, 'root' | 'type'>): NoteName => respellForType(scale.root, scale.type)

export const samePitch = (a: NoteName, b: NoteName) => getNoteChromaticIndex(a) === getNoteChromaticIndex(b)

const typeNames: Record<ScaleType, string> = {
  major: 'major',
  ionian: 'major',
  minor: 'minor',
  aeolian: 'minor',
  majorpentatonic: 'major pentatonic',
  minorpentatonic: 'minor pentatonic',
  harmonicminor: 'harmonic minor',
  melodicminor: 'melodic minor',
  dorian: 'Dorian',
  phrygian: 'Phrygian',
  lydian: 'Lydian',
  mixolydian: 'Mixolydian',
  locrian: 'Locrian',
}

const typeName = (type?: ScaleType) => typeNames[type ?? 'major']

const prettyRoot = (root: string) => root.replace(/#/g, '♯').replace(/b/g, '♭')
const spokenRoot = (root: string) => {
  const { letter, offset } = parse(root)
  const spoken = { [-2]: ' double flat', [-1]: ' flat', 0: '', 1: ' sharp', 2: ' double sharp' } as Record<
    number,
    string
  >
  return `${letter}${spoken[offset] ?? ''}`
}

/** "B♭ major", "F♯ Mixolydian", "A harmonic minor": the read-back (spec §4.3); not ScaleLib.getName's raw ids */
export const keyDisplayName = (scale: Pick<Scale, 'root' | 'type'>) =>
  `${prettyRoot(displayRoot(scale))} ${typeName(scale.type)}`

/** "B flat major": what a screen reader says for the read-back */
export const spokenKeyName = (scale: Pick<Scale, 'root' | 'type'>) =>
  `${spokenRoot(displayRoot(scale))} ${typeName(scale.type)}`

/** "B flat": what a screen reader says for a root cell */
export const spokenRootName = (root: NoteName | string) => spokenRoot(root)
